<template>
  <div class="question-answer-agent app-page app-page--fill">
    <div class="agent-panel">
      <div class="toolbar">
        <el-input
          v-model="searchKey"
          placeholder="搜索场景名称或提示词"
          clearable
          class="search-input"
          :prefix-icon="Search"
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        />
        <div class="toolbar-actions">
          <el-button v-if="can('新增')" type="primary" :icon="Plus" @click="openCreateDialog">新增</el-button>
          <el-button
            v-if="can('查询')"
            type="primary"
            circle
            size="small"
            class="app-refresh-btn"
            :icon="Refresh"
            :loading="loading"
            @click="loadData"
            title="刷新"
          />
        </div>
      </div>

      <el-table
        :data="tableData"
        border
        stripe
        v-loading="loading"
        class="agent-table"
        height="100%"
      >
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column label="场景名称" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.场景名称 || row.名称 || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="提示词" label="提示词" min-width="320" show-overflow-tooltip />
        <el-table-column prop="ISDELETED" label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.ISDELETED ? 'danger' : 'success'">
              {{ row.ISDELETED ? '已删除' : '正常' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="canAny('编辑', '删除')" label="操作" width="150" fixed="right" align="center">
          <template #default="{ row }">
            <el-button v-if="can('编辑')" type="primary" link size="small" :icon="Edit" @click="openEditDialog(row)">
              编辑
            </el-button>
            <el-button v-if="can('删除')" type="danger" link size="small" :icon="Delete" @click="handleDelete(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-bar">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          small
          @size-change="handlePageSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogMode === 'create' ? '新增问答智能体' : '编辑问答智能体'"
      width="640px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="90px">
        <el-form-item label="场景名称" prop="场景名称">
          <el-input v-model="formData.场景名称" placeholder="请输入场景名称" />
        </el-form-item>
        <el-form-item label="提示词" prop="提示词">
          <el-input
            v-model="formData.提示词"
            type="textarea"
            :rows="8"
            placeholder="请输入提示词"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button v-if="can('保存')" type="primary" :loading="saving" @click="handleSubmit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Edit, Plus, Refresh, Search } from '@element-plus/icons-vue'
import QuestionAnswerAgentApi, { type QuestionAnswerAgentItem } from '@/network/apis/QuestionAnswerAgent'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

useRouteTitle('问答智能体')
const { can, canAny } = useButtonPermission('/visualModel/QuestionAnswerAgent/index.vue')

const loading = ref(false)
const saving = ref(false)
const tableData = ref<QuestionAnswerAgentItem[]>([])
const searchKey = ref('')

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
})

const dialogVisible = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const formRef = ref()
const formData = reactive<QuestionAnswerAgentItem>({
  Id: undefined,
  场景名称: '',
  提示词: '',
  ISDELETED: true,
})

const formRules = {
  场景名称: [{ required: true, message: '请输入场景名称', trigger: 'blur' }],
}

const getResponseData = (res: any) => res?.response || res

const resetForm = () => {
  formData.Id = undefined
  formData.场景名称 = ''
  delete formData.名称
  formData.提示词 = ''
  formData.ISDELETED = true
}

const fillForm = (data: QuestionAnswerAgentItem = {}) => {
  resetForm()
  Object.assign(formData, {
    ...data,
    场景名称: data.场景名称 || data.名称 || '',
    ISDELETED: Boolean(data.ISDELETED),
  })
}

const buildSavePayload = () => {
  const { 名称, ...payload } = formData as QuestionAnswerAgentItem
  return {
    ...payload,
    场景名称: formData.场景名称 || '',
    ISDELETED: true,
  }
}

const loadData = async () => {
  loading.value = true
  try {
    const res: any = await QuestionAnswerAgentApi.list(
      {
        page: pagination.page,
        key: searchKey.value,
        intPageSize: pagination.pageSize,
      },
      []
    )
    const response = getResponseData(res)
    tableData.value = Array.isArray(response?.data) ? response.data : []
    pagination.total = Number(response?.dataCount || tableData.value.length || 0)
  } catch (error) {
    console.error('加载问答智能体列表失败:', error)
    tableData.value = []
    pagination.total = 0
    ElMessage.error('加载问答智能体列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.page = 1
  loadData()
}

const handlePageChange = (page: number) => {
  pagination.page = page
  loadData()
}

const handlePageSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  pagination.page = 1
  loadData()
}

const openCreateDialog = () => {
  dialogMode.value = 'create'
  resetForm()
  dialogVisible.value = true
}

const openEditDialog = async (row: QuestionAnswerAgentItem) => {
  const id = row.Id ?? row.id
  if (!id) {
    ElMessage.warning('缺少问答智能体ID，无法编辑')
    return
  }
  dialogMode.value = 'edit'
  dialogVisible.value = true
  saving.value = false
  try {
    const res: any = await QuestionAnswerAgentApi.detail(id)
    const response = getResponseData(res)
    fillForm(response || row)
  } catch (error) {
    console.error('查询问答智能体详情失败:', error)
    fillForm(row)
    ElMessage.error('查询详情失败，已使用列表数据')
  }
}

const handleSubmit = () => {
  formRef.value?.validate(async (valid: boolean) => {
    if (!valid) return
    saving.value = true
    try {
      const payload = buildSavePayload()
      const res: any = await QuestionAnswerAgentApi.save(payload)
      if (res?.success === false) {
        ElMessage.error(res?.msg || '保存失败')
        return
      }
      ElMessage.success(dialogMode.value === 'create' ? '新增成功' : '更新成功')
      dialogVisible.value = false
      loadData()
    } catch (error) {
      console.error('保存问答智能体失败:', error)
      ElMessage.error('保存失败')
    } finally {
      saving.value = false
    }
  })
}

const handleDelete = (row: QuestionAnswerAgentItem) => {
  const id = row.Id ?? row.id
  if (!id) {
    ElMessage.warning('缺少问答智能体ID，无法删除')
    return
  }
  ElMessageBox.confirm('确认删除该问答智能体吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  })
    .then(async () => {
      try {
        const res: any = await QuestionAnswerAgentApi.delete(id)
        if (res?.success === false) {
          ElMessage.error(res?.msg || '删除失败')
          return
        }
        ElMessage.success('删除成功')
        loadData()
      } catch (error) {
        console.error('删除问答智能体失败:', error)
        ElMessage.error('删除失败')
      }
    })
    .catch(() => {})
}

onMounted(() => {
  loadData()
})
</script>

<style scoped lang="less">
.question-answer-agent {
  .agent-panel {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .search-input {
    max-width: 360px;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .agent-table {
    flex: 1;
    min-height: 0;
  }

  .pagination-bar {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
